/**
 * 🚀 FikoSharedMemory - FAZA 3.3: SharedArrayBuffer Implementation
 *
 * Zero-copy data sharing between main thread and workers.
 * Eliminates 70ms+ overhead from data transfer in large geometry operations.
 *
 * Requirements:
 * - HTTPS (already satisfied)
 * - COOP/COEP headers (Cross-Origin-Opener-Policy, Cross-Origin-Embedder-Policy)
 * - Atomic synchronization for thread-safe access
 *
 * Features:
 * - Shared geometry buffers (positions, normals, indices)
 * - Lock-free ring buffers for task queuing
 * - Atomic counters for progress tracking
 * - Memory pool for reusable shared buffers
 */

// ============================================================================
// FEATURE DETECTION
// ============================================================================

// 🚀 PERFORMANS OPT: Cache the availability check result
let _sharedArrayBufferAvailable: boolean | null = null;

/**
 * Check if SharedArrayBuffer is available
 * Requires COOP/COEP headers in production
 */
export function isSharedArrayBufferAvailable(): boolean {
    // Return cached result if available
    if (_sharedArrayBufferAvailable !== null) {
        return _sharedArrayBufferAvailable;
    }

    try {
        // Check if SharedArrayBuffer exists
        if (typeof SharedArrayBuffer === 'undefined') {
            _sharedArrayBufferAvailable = false;
            return false;
        }

        // Try to create a small test buffer
        const testBuffer = new SharedArrayBuffer(8);
        const testView = new Int32Array(testBuffer);

        // Test atomic operations
        Atomics.store(testView, 0, 1);
        _sharedArrayBufferAvailable = Atomics.load(testView, 0) === 1;
        return _sharedArrayBufferAvailable;
    } catch (e) {
        _sharedArrayBufferAvailable = false;
        return false;
    }
}

/**
 * Check if required headers are present
 */
export function hasRequiredHeaders(): boolean {
    // crossOriginIsolated is true when COOP/COEP headers are properly set
    return typeof crossOriginIsolated !== 'undefined' && crossOriginIsolated;
}

// ============================================================================
// SHARED BUFFER TYPES
// ============================================================================

export interface SharedBufferDescriptor {
    id: string;
    buffer: SharedArrayBuffer;
    byteLength: number;
    elementType: 'float32' | 'int32' | 'uint32' | 'uint8';
    elementCount: number;
    inUse: boolean;
    timestamp: number;
}

export interface SharedGeometry {
    id: string;
    positions: SharedBufferDescriptor;
    normals?: SharedBufferDescriptor;
    indices?: SharedBufferDescriptor;
    uvs?: SharedBufferDescriptor;
    vertexCount: number;
    triangleCount: number;
}

export interface SharedMemoryStats {
    totalAllocated: number;
    inUse: number;
    pooled: number;
    bufferCount: number;
    isAvailable: boolean;
}

// ============================================================================
// ATOMIC LOCK
// ============================================================================

/**
 * Simple spinlock using Atomics
 * For protecting critical sections when accessing shared memory
 */
export class AtomicLock {
    private lockBuffer: SharedArrayBuffer;
    private lockView: Int32Array;
    private readonly UNLOCKED = 0;
    private readonly LOCKED = 1;

    constructor(sharedBuffer?: SharedArrayBuffer) {
        if (sharedBuffer) {
            this.lockBuffer = sharedBuffer;
        } else {
            this.lockBuffer = new SharedArrayBuffer(4);
        }
        this.lockView = new Int32Array(this.lockBuffer);
    }

    /**
     * Acquire lock (blocking)
     * 🚀 PERFORMANS OPT: Exponential backoff to reduce CPU burn
     */
    acquire(): void {
        let spinCount = 0;
        const MAX_SPINS = 100;

        while (Atomics.compareExchange(this.lockView, 0, this.UNLOCKED, this.LOCKED) !== this.UNLOCKED) {
            spinCount++;

            if (spinCount < MAX_SPINS) {
                // Spin a bit first - fast path for uncontended locks
                for (let i = 0; i < spinCount * 10; i++) { /* spin */ }
            } else {
                // After MAX_SPINS, start waiting with increasing timeouts
                const timeout = Math.min(spinCount - MAX_SPINS, 10);
                Atomics.wait(this.lockView, 0, this.LOCKED, timeout);
            }
        }
    }

    /**
     * Try to acquire lock (non-blocking)
     */
    tryAcquire(): boolean {
        return Atomics.compareExchange(this.lockView, 0, this.UNLOCKED, this.LOCKED) === this.UNLOCKED;
    }

    /**
     * Release lock
     */
    release(): void {
        Atomics.store(this.lockView, 0, this.UNLOCKED);
        Atomics.notify(this.lockView, 0, 1);
    }

    /**
     * Get underlying buffer (for sharing with workers)
     */
    getBuffer(): SharedArrayBuffer {
        return this.lockBuffer;
    }
}

// ============================================================================
// ATOMIC COUNTER
// ============================================================================

/**
 * Thread-safe counter using Atomics
 * Useful for progress tracking across threads
 */
export class AtomicCounter {
    private counterBuffer: SharedArrayBuffer;
    private counterView: Int32Array;

    constructor(sharedBuffer?: SharedArrayBuffer, initialValue: number = 0) {
        if (sharedBuffer) {
            this.counterBuffer = sharedBuffer;
        } else {
            this.counterBuffer = new SharedArrayBuffer(4);
        }
        this.counterView = new Int32Array(this.counterBuffer);
        if (!sharedBuffer) {
            Atomics.store(this.counterView, 0, initialValue);
        }
    }

    get(): number {
        return Atomics.load(this.counterView, 0);
    }

    set(value: number): void {
        Atomics.store(this.counterView, 0, value);
    }

    increment(): number {
        return Atomics.add(this.counterView, 0, 1) + 1;
    }

    decrement(): number {
        return Atomics.sub(this.counterView, 0, 1) - 1;
    }

    add(value: number): number {
        return Atomics.add(this.counterView, 0, value) + value;
    }

    getBuffer(): SharedArrayBuffer {
        return this.counterBuffer;
    }
}

// ============================================================================
// SHARED RING BUFFER
// ============================================================================

/**
 * Lock-free ring buffer for task queuing
 * Single producer, single consumer (SPSC)
 */
export class SharedRingBuffer {
    private buffer: SharedArrayBuffer;
    private dataView: Float32Array;
    private metaView: Int32Array;

    // Meta indices
    private static readonly HEAD_INDEX = 0;
    private static readonly TAIL_INDEX = 1;
    private static readonly SIZE_INDEX = 2;

    private readonly capacity: number;
    private readonly metaSize = 12; // 3 * 4 bytes for head, tail, size

    constructor(capacity: number, sharedBuffer?: SharedArrayBuffer) {
        this.capacity = capacity;
        const dataBytes = capacity * 4; // Float32
        const totalBytes = this.metaSize + dataBytes;

        if (sharedBuffer) {
            this.buffer = sharedBuffer;
        } else {
            this.buffer = new SharedArrayBuffer(totalBytes);
        }

        this.metaView = new Int32Array(this.buffer, 0, 3);
        this.dataView = new Float32Array(this.buffer, this.metaSize, capacity);

        if (!sharedBuffer) {
            Atomics.store(this.metaView, SharedRingBuffer.HEAD_INDEX, 0);
            Atomics.store(this.metaView, SharedRingBuffer.TAIL_INDEX, 0);
            Atomics.store(this.metaView, SharedRingBuffer.SIZE_INDEX, 0);
        }
    }

    /**
     * Push value to buffer (producer side)
     */
    push(value: number): boolean {
        const head = Atomics.load(this.metaView, SharedRingBuffer.HEAD_INDEX);
        const tail = Atomics.load(this.metaView, SharedRingBuffer.TAIL_INDEX);
        const size = Atomics.load(this.metaView, SharedRingBuffer.SIZE_INDEX);

        if (size >= this.capacity) {
            return false; // Buffer full
        }

        this.dataView[head] = value;
        Atomics.store(this.metaView, SharedRingBuffer.HEAD_INDEX, (head + 1) % this.capacity);
        Atomics.add(this.metaView, SharedRingBuffer.SIZE_INDEX, 1);

        return true;
    }

    /**
     * Pop value from buffer (consumer side)
     */
    pop(): number | null {
        const size = Atomics.load(this.metaView, SharedRingBuffer.SIZE_INDEX);

        if (size === 0) {
            return null; // Buffer empty
        }

        const tail = Atomics.load(this.metaView, SharedRingBuffer.TAIL_INDEX);
        const value = this.dataView[tail];

        Atomics.store(this.metaView, SharedRingBuffer.TAIL_INDEX, (tail + 1) % this.capacity);
        Atomics.sub(this.metaView, SharedRingBuffer.SIZE_INDEX, 1);

        return value;
    }

    /**
     * Get current size
     */
    size(): number {
        return Atomics.load(this.metaView, SharedRingBuffer.SIZE_INDEX);
    }

    /**
     * Check if empty
     */
    isEmpty(): boolean {
        return this.size() === 0;
    }

    /**
     * Check if full
     */
    isFull(): boolean {
        return this.size() >= this.capacity;
    }

    /**
     * Get underlying buffer
     */
    getBuffer(): SharedArrayBuffer {
        return this.buffer;
    }
}

// ============================================================================
// SHARED MEMORY MANAGER
// ============================================================================

/**
 * Main shared memory manager
 * Handles allocation, pooling, and lifecycle of shared buffers
 */
export class FikoSharedMemory {
    private static instance: FikoSharedMemory | null = null;

    private isAvailable: boolean;
    private buffers: Map<string, SharedBufferDescriptor> = new Map();
    private geometries: Map<string, SharedGeometry> = new Map();

    // Buffer pool for reuse
    private bufferPool: Map<number, SharedArrayBuffer[]> = new Map();

    // Stats
    private stats: SharedMemoryStats = {
        totalAllocated: 0,
        inUse: 0,
        pooled: 0,
        bufferCount: 0,
        isAvailable: false
    };

    // Pool configuration
    private readonly MAX_POOL_SIZE_PER_BUCKET = 5;
    private readonly POOL_SIZE_BUCKETS = [
        1024,           // 1KB
        16384,          // 16KB
        65536,          // 64KB
        262144,         // 256KB
        1048576,        // 1MB
        4194304,        // 4MB
        16777216        // 16MB
    ];

    private constructor() {
        this.isAvailable = isSharedArrayBufferAvailable();
        this.stats.isAvailable = this.isAvailable;

        if (!this.isAvailable) {
            console.warn('[FikoSharedMemory] SharedArrayBuffer not available. Falling back to standard transfers.');
        }
    }

    /**
     * Get singleton instance
     */
    static getInstance(): FikoSharedMemory {
        if (!FikoSharedMemory.instance) {
            FikoSharedMemory.instance = new FikoSharedMemory();
        }
        return FikoSharedMemory.instance;
    }

    /**
     * Check if shared memory is available
     */
    get available(): boolean {
        return this.isAvailable;
    }

    // ========================================================================
    // BUFFER ALLOCATION
    // ========================================================================

    /**
     * Allocate a shared buffer
     */
    allocateBuffer(
        id: string,
        byteLength: number,
        elementType: SharedBufferDescriptor['elementType'] = 'float32'
    ): SharedBufferDescriptor | null {
        if (!this.isAvailable) {
            return null;
        }

        // Try to get from pool first
        const pooledBuffer = this.getFromPool(byteLength);

        const buffer = pooledBuffer || new SharedArrayBuffer(byteLength);
        const elementSize = this.getElementSize(elementType);

        const descriptor: SharedBufferDescriptor = {
            id,
            buffer,
            byteLength,
            elementType,
            elementCount: Math.floor(byteLength / elementSize),
            inUse: true,
            timestamp: performance.now()
        };

        this.buffers.set(id, descriptor);

        // Update stats
        if (!pooledBuffer) {
            this.stats.totalAllocated += byteLength;
        }
        this.stats.inUse += byteLength;
        this.stats.bufferCount++;

        return descriptor;
    }

    /**
     * Allocate a Float32 buffer
     */
    allocateFloat32(id: string, elementCount: number): Float32Array | null {
        const descriptor = this.allocateBuffer(id, elementCount * 4, 'float32');
        if (!descriptor) return null;
        return new Float32Array(descriptor.buffer);
    }

    /**
     * Allocate a Uint32 buffer
     */
    allocateUint32(id: string, elementCount: number): Uint32Array | null {
        const descriptor = this.allocateBuffer(id, elementCount * 4, 'uint32');
        if (!descriptor) return null;
        return new Uint32Array(descriptor.buffer);
    }

    /**
     * Allocate an Int32 buffer
     */
    allocateInt32(id: string, elementCount: number): Int32Array | null {
        const descriptor = this.allocateBuffer(id, elementCount * 4, 'int32');
        if (!descriptor) return null;
        return new Int32Array(descriptor.buffer);
    }

    // ========================================================================
    // GEOMETRY ALLOCATION
    // ========================================================================

    /**
     * Allocate shared geometry buffers
     */
    allocateGeometry(
        id: string,
        vertexCount: number,
        hasNormals: boolean = true,
        indexCount: number = 0,
        hasUVs: boolean = false
    ): SharedGeometry | null {
        if (!this.isAvailable) {
            return null;
        }

        const geometry: SharedGeometry = {
            id,
            positions: this.allocateBuffer(`${id}_positions`, vertexCount * 3 * 4, 'float32')!,
            vertexCount,
            triangleCount: indexCount > 0 ? indexCount / 3 : vertexCount / 3
        };

        if (!geometry.positions) {
            return null;
        }

        if (hasNormals) {
            geometry.normals = this.allocateBuffer(`${id}_normals`, vertexCount * 3 * 4, 'float32') || undefined;
        }

        if (indexCount > 0) {
            geometry.indices = this.allocateBuffer(`${id}_indices`, indexCount * 4, 'uint32') || undefined;
        }

        if (hasUVs) {
            geometry.uvs = this.allocateBuffer(`${id}_uvs`, vertexCount * 2 * 4, 'float32') || undefined;
        }

        this.geometries.set(id, geometry);
        return geometry;
    }

    /**
     * Get geometry by ID
     */
    getGeometry(id: string): SharedGeometry | null {
        return this.geometries.get(id) || null;
    }

    // ========================================================================
    // BUFFER POOLING
    // ========================================================================

    /**
     * Return buffer to pool for reuse
     */
    releaseBuffer(id: string): void {
        const descriptor = this.buffers.get(id);
        if (!descriptor) return;

        descriptor.inUse = false;
        this.stats.inUse -= descriptor.byteLength;

        // Add to pool if not too large
        if (descriptor.byteLength <= 16777216) { // 16MB max
            this.addToPool(descriptor.buffer);
            this.stats.pooled += descriptor.byteLength;
        }

        this.buffers.delete(id);
        this.stats.bufferCount--;
    }

    /**
     * Release geometry and its buffers
     */
    releaseGeometry(id: string): void {
        const geometry = this.geometries.get(id);
        if (!geometry) return;

        this.releaseBuffer(geometry.positions.id);
        if (geometry.normals) this.releaseBuffer(geometry.normals.id);
        if (geometry.indices) this.releaseBuffer(geometry.indices.id);
        if (geometry.uvs) this.releaseBuffer(geometry.uvs.id);

        this.geometries.delete(id);
    }

    /**
     * Add buffer to pool
     */
    private addToPool(buffer: SharedArrayBuffer): void {
        const bucket = this.findBucket(buffer.byteLength);
        let pool = this.bufferPool.get(bucket);

        if (!pool) {
            pool = [];
            this.bufferPool.set(bucket, pool);
        }

        if (pool.length < this.MAX_POOL_SIZE_PER_BUCKET) {
            pool.push(buffer);
        }
    }

    /**
     * Get buffer from pool
     */
    private getFromPool(byteLength: number): SharedArrayBuffer | null {
        const bucket = this.findBucket(byteLength);
        const pool = this.bufferPool.get(bucket);

        if (pool && pool.length > 0) {
            const buffer = pool.pop()!;
            this.stats.pooled -= buffer.byteLength;
            return buffer;
        }

        return null;
    }

    /**
     * Find appropriate pool bucket for size
     */
    private findBucket(byteLength: number): number {
        for (const bucket of this.POOL_SIZE_BUCKETS) {
            if (byteLength <= bucket) {
                return bucket;
            }
        }
        return this.POOL_SIZE_BUCKETS[this.POOL_SIZE_BUCKETS.length - 1];
    }

    // ========================================================================
    // UTILITY
    // ========================================================================

    /**
     * Get element size in bytes
     */
    private getElementSize(elementType: SharedBufferDescriptor['elementType']): number {
        switch (elementType) {
            case 'float32':
            case 'int32':
            case 'uint32':
                return 4;
            case 'uint8':
                return 1;
            default:
                return 4;
        }
    }

    /**
     * Get buffer by ID
     */
    getBuffer(id: string): SharedBufferDescriptor | null {
        return this.buffers.get(id) || null;
    }

    /**
     * Get statistics
     */
    getStats(): SharedMemoryStats {
        return { ...this.stats };
    }

    /**
     * Create atomic lock
     */
    createLock(): AtomicLock | null {
        if (!this.isAvailable) return null;
        return new AtomicLock();
    }

    /**
     * Create atomic counter
     */
    createCounter(initialValue: number = 0): AtomicCounter | null {
        if (!this.isAvailable) return null;
        return new AtomicCounter(undefined, initialValue);
    }

    /**
     * Create ring buffer
     */
    createRingBuffer(capacity: number): SharedRingBuffer | null {
        if (!this.isAvailable) return null;
        return new SharedRingBuffer(capacity);
    }

    /**
     * Dispose and cleanup
     */
    dispose(): void {
        this.buffers.clear();
        this.geometries.clear();
        this.bufferPool.clear();

        this.stats = {
            totalAllocated: 0,
            inUse: 0,
            pooled: 0,
            bufferCount: 0,
            isAvailable: this.isAvailable
        };

        FikoSharedMemory.instance = null;
    }
}

// ============================================================================
// EXPORTS
// ============================================================================

export const sharedMemory = FikoSharedMemory.getInstance();

/**
 * Helper to check if SharedArrayBuffer should be used
 */
export function shouldUseSharedMemory(): boolean {
    return isSharedArrayBufferAvailable() && hasRequiredHeaders();
}

/**
 * Create a shared Float32Array from data
 * Returns regular Float32Array if SharedArrayBuffer not available
 */
export function createSharedFloat32Array(data: Float32Array | number[]): Float32Array {
    if (!isSharedArrayBufferAvailable()) {
        return new Float32Array(data);
    }

    const length = data.length;
    const sharedBuffer = new SharedArrayBuffer(length * 4);
    const sharedArray = new Float32Array(sharedBuffer);
    sharedArray.set(data);
    return sharedArray;
}

/**
 * Create a shared Uint32Array from data
 */
export function createSharedUint32Array(data: Uint32Array | number[]): Uint32Array {
    if (!isSharedArrayBufferAvailable()) {
        return new Uint32Array(data);
    }

    const length = data.length;
    const sharedBuffer = new SharedArrayBuffer(length * 4);
    const sharedArray = new Uint32Array(sharedBuffer);
    sharedArray.set(data);
    return sharedArray;
}
